import { DatePicker, TimePicker } from "@material-ui/pickers";
import { makeStyles, withStyles } from "@material-ui/styles";
import React, { useCallback } from "react";
import {
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useEditContext,
  useTranslate,
} from "react-admin";
import resourceNames from "../../../../common/data/resourceNames";
import { FormToolbar } from "../../../common/form/FormToolbar";
import { GenericDateOrTimePicker } from "../../../common/form/GenericDateOrTimePicker";
import classnames from "classnames";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useAppData } from "../../../../common/hooks/useAppData";
import { ProLogsAutocompleteInput } from "../../../common/form/ProLogsAutocompleteInput";
import { parseISO } from "date-fns";
import { isNumber, memoize } from "lodash";

const useStyles = makeStyles(
  (theme) => ({
    container: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      marginBottom: "20px",
    },
    icon: {
      width: "9em",
      height: "9em",
    },
    message: {
      textAlign: "center",
      fontFamily: "Roboto, sans-serif",
      opacity: 0.5,
      margin: "0 1em",
    },
  }),
  { name: "RaLoading" }
);

interface LoadingProps {
  className?: string;
  loadingPrimary?: string;
  loadingSecondary?: string;
}

interface Props {
  timeZone?: string | null;
  record?: any;
}

const AutocompleteInputInDialog = withStyles({
  suggestionsContainer: { zIndex: 2000 },
})(ProLogsAutocompleteInput);

const Loading = ({
  loadingPrimary = "ra.page.loading",
  loadingSecondary = "ra.message.loading",
  ...props
}: LoadingProps) => {
  const { className } = props;
  const classes = useStyles(props);
  const translate = useTranslate();
  return (
    <div className={classnames(classes.container, className)}>
      <div className={classes.message}>
        <CircularProgress className={classes.icon} color="primary" />
        <h1>{translate(loadingPrimary)}</h1>
        <div>{translate(loadingSecondary)}.</div>
      </div>
    </div>
  );
};

const minValue = memoize(
  (min: string, message = "ra.validation.minValue") => (
    value?: number | string,
    values?: any
  ) => {
    if (!value) {
      return undefined;
    }

    let valueToCheck: number;
    if (!isNumber(value)) {
      valueToCheck = parseISO(value).getTime();
    } else {
      valueToCheck = value;
    }

    const retVal = valueToCheck < parseISO(min).getTime() ? message : undefined;

    return retVal;
  }
);

const maxValue = memoize(
  (max: string, message = "ra.validation.maxValue") => (
    value?: number | string,
    values?: any
  ) => {
    if (!value) {
      return undefined;
    }

    let valueToCheck: number;
    if (!isNumber(value)) {
      valueToCheck = parseISO(value).getTime();
    } else {
      valueToCheck = value;
    }

    const retVal = valueToCheck > parseISO(max).getTime() ? message : undefined;

    return retVal;
  }
);

export const DriverLogEventEditForm = ({ timeZone, ...props }: Props) => {
  const { loading } = useEditContext();
  const countriesAndStates = useAppData((x) => x.countries) ?? [];
  const countries = countriesAndStates.map((x) => ({ id: x.id, name: x.name }));
  const appDataDutyStatuses = useAppData((x) => x.dutyStatuses) ?? [];
  const dutyStatuses = appDataDutyStatuses.map((x) => ({
    name: `appData.dutyCycles.${x}`,
    id: x,
  }));

  const translate = useTranslate();
  const filterChoicesByDriverIds = useCallback(
    (choices?: any[], optionValue?: string) => {
      if (!props.record?.driverId) {
        return choices;
      }

      if (!choices) {
        return choices;
      }

      const driverIds: Array<number> = [props.record.driverId];
      if (props.record.coDriverId) {
        driverIds.push(props.record.coDriverId);
      }

      return choices.filter(
        (x) => driverIds.indexOf(x[optionValue ?? "id"]) !== -1
      );
    },
    [props.record]
  );

  if (loading) {
    return <Loading />;
  }

  if (props.record.origin !== "AutomaticallyRecordedByEld") {
    return (
      <div className="form-create-edit-drawer">
        <SimpleForm variant="outlined" toolbar={<FormToolbar />} {...props}>
          <TextInput source="notes" />
          <GenericDateOrTimePicker
            PickerComponent={DatePicker}
            source="date"
            disabled
            timeZone={timeZone}
          />
          <GenericDateOrTimePicker
            disabled={!props.record?.minTime || !props.record?.maxTime}
            PickerComponent={TimePicker}
            source="time"
            label="Time"
            timeZone={timeZone}
            options={{
              views: ["hours", "minutes", "seconds"],
            }}
            validate={[
              required(),
              minValue(
                props.record.minTime,
                translate("ra.validation.minValue", {
                  min: parseISO(props.record.minTime).toLocaleTimeString(
                    "en-US",
                    {
                      timeZone: timeZone ?? undefined,
                    }
                  ),
                })
              ),
              maxValue(
                props.record.maxTime,
                translate("ra.validation.maxValue", {
                  max: parseISO(props.record.maxTime).toLocaleTimeString(
                    "en-US",
                    {
                      timeZone: timeZone ?? undefined,
                    }
                  ),
                })
              ),
            ]}
          />
          <SelectInput
            source="dutyStatus"
            choices={dutyStatuses}
            validate={required()}
          />
          <TextInput source="location" validate={required()} />
          <SelectInput
            source="country"
            choices={countries}
            translate="no"
            validate={required()}
          />
          <FormDataConsumer>
            {({ scopedFormData, formData, getSource, ...rest }) => (
              <SelectInput
                validate={required()}
                source="state"
                translate="no"
                choices={
                  countriesAndStates.find((x) => x.id === formData["country"])
                    ?.states ?? []
                }
                {...rest}
              />
            )}
          </FormDataConsumer>
          <TextInput source="reasonForEdit" validate={required()} />
        </SimpleForm>
      </div>
    );
  }

  return (
    <div className="form-create-edit-drawer">
      <SimpleForm variant="outlined" toolbar={<FormToolbar />} {...props}>
        <TextInput source="notes" />
        <ReferenceInput
          source="driverId"
          label="resources.hos-unidentified-driving.labels.driver"
          reference={resourceNames.ADMIN_DRIVERS}>
          <AutocompleteInputInDialog
            optionText="driverName"
            translateChoice={false}
            choicesFilter={filterChoicesByDriverIds}
          />
        </ReferenceInput>
      </SimpleForm>
    </div>
  );
};
